import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { UserService } from 'src/app/shared/services/user.service';
import { User } from '../user.model';
import { UserCreateOrEditModalComponent } from '../user-create-or-edit-modal/user-create-or-edit-modal.component';
import { Organization } from '../../organization/organization-models';
import { ActionLogModalComponent } from 'src/app/shared/components/modals/actionlog-modal/action-log-modal/action-log-modal.component';
import { EntityType } from '../../prices/action-log';
import { DepartmentRestrictionModalComponent } from '../department-restriction-modal/department-restriction-modal.component';
import { RouterLink } from '@angular/router';
import { RequiresFeatureRoleDirective } from '../../../shared/directives/requires-feature-role.directive';
import { TooltipListPipe } from '../../../shared/pipes/tooltipList.pipe';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'adm-user-row',
  templateUrl: './user-row.component.html',
  styleUrls: ['./user-row.component.scss'],
  imports: [
    RouterLink,
    RequiresFeatureRoleDirective,
    TooltipListPipe,
    CommonModule,
    MatCardModule,
    MatTooltipModule,
    MatBadgeModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
  ],
})
export class UserRowComponent {
  private dialog = inject(MatDialog);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private userService = inject(UserService);
  private snackbarService = inject(SnackbarService);

  @Input()
  public user!: User;
  @Input()
  public organization!: Organization;
  @Output()
  refreshAfterUpdateEvent = new EventEmitter<User>();
  @Output()
  refreshAfterDeleteEvent = new EventEmitter<User>();

  public roles = this.environmentLoaderService.virtualRoles;

  public showUserActionLog(user: User) {
    this.dialog.open(ActionLogModalComponent, {
      data: { entityId: user.id, entityType: EntityType.PortalUser, noOrganisationSelector: true },
      width: '1024px',
    });
  }
  public openEditUserModal(user: User) {
    const dialogRef = this.dialog.open(UserCreateOrEditModalComponent, {
      data: { organizationId: user.organizationId, userId: user.id },
    });

    dialogRef.afterClosed().subscribe(user => {
      if (user) {
        this.refreshAfterUpdateEvent.emit(user);
      }
    });
  }

  public toggleActivationOnUser(user: User) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: user.enabled ? `Deaktiver bruger` : `Aktiver bruger`,
        text: `Er du sikker på at du vil ${user.enabled ? `deaktivere` : `aktivere`} brugeren ${user.firstName} ${user.lastName}?`,
        confirmText: `Ja`,
        denyText: `Nej`,
        icon: 'block',
      },
      disableClose: true,
    });

    confirmDialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.userService.toggleActivationOnUser(user.organizationId, user.id, user.enabled).subscribe({
          next: () => {
            this.refreshAfterDeleteEvent.emit(user);
            this.snackbarService.success(`Bruger blev succesfuldt ${user.enabled ? `deaktiveret` : `aktiveret`}.`);
          },
          error: err => {
            this.snackbarService.error(`Der skete fejl under ${user.enabled ? `deaktivering` : `aktivering`} (${err.error.code})`);
          },
        });
      }
    });
  }

  public resendInvitation(user: User) {
    this.userService.resendInvitation(user.id).subscribe({
      next: () => {
        this.snackbarService.success(`Velkomstmail blev gensendt.`);
      },
      error: err => {
        if (err.error?.code === 100720) {
          this.snackbarService.error(`Brugeren kunne ikke findes.`);
        } else {
          this.snackbarService.error(`Der skete en fejl under gensendelse af velkomstmail.`);
        }
      },
    });
  }

  public departmentRestriction(user: User) {
    const confirmDialogRef = this.dialog.open(DepartmentRestrictionModalComponent, {
      data: {
        user: user,
      },
      disableClose: true,
      width: '1024px',
    });

    confirmDialogRef.afterClosed().subscribe(updatedUser => {
      if (updatedUser) {
        this.refreshAfterUpdateEvent.emit(user);
      }
    });
  }
}
